import { sendAnalytics } from '@audio-network/analytics'
import { RefObject, useEffect, useRef, useState } from 'react'

import { clientGetRelatedTracks } from '@/client/track/getRelatedTracks'
import { clientGetSimilarTracks } from '@/client/track/getSimilarTracks'

import {
	HandpickedFetchState,
	UseHandpickedTracksResult,
} from './TrackRelatedTracks.types'

export const useHandpickedTracks = (
	trackId: number,
	handpickedTracksFromProps: Track[] | undefined,
	onHandpickedFetchBegin?: () => void
): UseHandpickedTracksResult => {
	const shouldFetchHandpickedTracks = handpickedTracksFromProps === undefined
	const [fetchState, setFetchState] = useState<HandpickedFetchState>({
		forTrackId: trackId,
		handpickedTracks: null,
	})
	const onHandpickedFetchBeginRef = useRef(onHandpickedFetchBegin)
	onHandpickedFetchBeginRef.current = onHandpickedFetchBegin

	useEffect(() => {
		if (!shouldFetchHandpickedTracks) {
			return undefined
		}

		setFetchState({ forTrackId: trackId, handpickedTracks: null })
		onHandpickedFetchBeginRef.current?.()

		let cancelled = false

		const fetchHandpickedTracks = async (): Promise<void> => {
			const results = await clientGetRelatedTracks(trackId)

			if (cancelled) {
				return
			}

			setFetchState({ forTrackId: trackId, handpickedTracks: results })
		}

		void fetchHandpickedTracks()

		return (): void => {
			cancelled = true
		}
	}, [shouldFetchHandpickedTracks, trackId])

	if (!shouldFetchHandpickedTracks) {
		return { handpickedTracks: handpickedTracksFromProps ?? [] }
	}

	const handpickedTracks =
		fetchState.forTrackId === trackId ? fetchState.handpickedTracks : null

	return { handpickedTracks }
}

const fetchSimilarTracksForTrack = async (
	trackId: number,
	handpickedTracks: Track[] | null
): Promise<Track[]> => {
	const tracks = await clientGetSimilarTracks([trackId])

	if (!tracks) {
		return []
	}

	if (!handpickedTracks) {
		return tracks
	}

	return tracks.filter(
		(similarTrack) =>
			!handpickedTracks.some(
				(handpickedTrack) => handpickedTrack.id === similarTrack.id
			)
	)
}

const isTabPanelVisible = (element: HTMLElement): boolean => {
	const panel = element.closest('[role="tabpanel"]')
	if (!panel) {
		return true
	}

	return panel.getAttribute('aria-hidden') !== 'true'
}

export const useShowAISimilarTracks = (
	containerRef: RefObject<HTMLElement>,
	trackId: number,
	handpickedTracks: Track[] | null
): Track[] | null => {
	const [similarTracks, setSimilarTracks] = useState<Track[] | null>(null)
	const hasLoadedKeyRef = useRef<string | null>(null)

	const handpickedIdsKey =
		handpickedTracks == null
			? null
			: handpickedTracks.map((handpickedTrack) => handpickedTrack.id).join(',')

	useEffect(() => {
		setSimilarTracks(null)
		hasLoadedKeyRef.current = null
	}, [trackId, handpickedIdsKey])

	useEffect(() => {
		const container = containerRef.current
		if (!container || handpickedIdsKey === null) {
			return undefined
		}

		let cancelled = false
		const loadKey = `${trackId}:${handpickedIdsKey}`

		const loadSimilarTracks = async (): Promise<void> => {
			if (!isTabPanelVisible(container)) {
				return
			}

			if (hasLoadedKeyRef.current === loadKey) {
				return
			}

			const results = await fetchSimilarTracksForTrack(
				trackId,
				handpickedTracks
			)
			if (cancelled) {
				return
			}

			hasLoadedKeyRef.current = loadKey

			sendAnalytics({
				noun: 'Similar Tracks',
				pastTenseVerb: 'Completed',
				eventParameters: {
					ResultsCount: results.length,
				},
			})

			setSimilarTracks(results)
		}

		const handleVisibilityChange = (): void => {
			void loadSimilarTracks()
		}

		handleVisibilityChange()

		const panel = container.closest('[role="tabpanel"]')
		if (!panel) {
			return () => {
				cancelled = true
			}
		}

		const observer = new MutationObserver(handleVisibilityChange)
		observer.observe(panel, {
			attributes: true,
			attributeFilter: ['aria-hidden'],
		})

		return () => {
			cancelled = true
			observer.disconnect()
		}
	}, [containerRef, handpickedIdsKey, handpickedTracks, trackId])

	if (handpickedTracks === null) {
		return null
	}

	return similarTracks
}
